<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { CasesService } from '@/types/generated/services.gen'
  import {
    type CasesPartialUpdateData,
    type CustomerCase as Case,
    type GenericDataMapper,
    type LawData
  } from '@/types/generated/types.gen'
  import { Switch } from '@/components/ui/switch'
  import '@tato30/vue-pdf/style.css'
  import SearchIcon from '@/components/icons/SearchIcon.vue'

  import DatamapperTable from '@/components/app/cases/DatamapperTable.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { debounce } from '@/lib/timing'

  const caseStore = useCaseStore()
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const nonEmptyMappers = computed<GenericDataMapper[]>(() => caseStore.nonEmptyLawSpecificMappers)
  const lawSpecificMappers = computed<GenericDataMapper[]>(() => caseStore.lawSpecificMappers)

  const showMappersWithValues = ref(true)
  const search = ref('')

  const filteredMappers = computed<GenericDataMapper[]>(() => {
    const mappers = showMappersWithValues?.value ? nonEmptyMappers : lawSpecificMappers
    return mappers.value?.filter((mapper) => {
      return mapper.label.toLowerCase().includes(search.value.toLowerCase())
    })
  })

  const handleSwitchChange = (value: boolean) => {
    showMappersWithValues.value = value
  }

  const shouldDisplayMapperKeys = computed<string[]>(() => {
    return filteredMappers.value.map((mapper) => mapper.key)
  })

  const handleSave = debounce(async (key: string, mapperKey: string, value: any) => {
    console.log('handling save', key, mapperKey, value)
    const [parent, field] = mapperKey.split('.')
    const data = field ? { [parent]: { [field]: value } } : { [mapperKey]: value }
    const result: Case = (await CasesService.casesPartialUpdate({
      uuid: caseRecord.value?.uuid as string,
      requestBody: {
        data: data
      }
    } as CasesPartialUpdateData)) as Case

    // Update the case changes through the store
    if (result.data.changes) {
      caseStore.updateCaseChanges(result.data.changes)
    }
  }, 1000)
</script>

<template>
  <div
    class="mt-4 flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700
      dark:bg-neutral-900 dark:text-neutral-400 md:p-5"
  >
    <div>
      <div class="grid gap-y-2 pb-5 ps-1 pt-1 md:grid-cols-2 md:gap-x-5 md:gap-y-0">
        <div>
          <!-- Search Input -->
          <div class="relative">
            <div
              class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-3.5"
            >
              <SearchIcon class="size-4 flex-shrink-0 text-gray-500" />
            </div>
            <input
              v-model="search"
              class="block w-full rounded-lg border-transparent bg-gray-100 px-3 py-[7px] ps-10 text-sm
                focus:border-purple-500 focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
              type="text"
              :placeholder="'Suche nach Feld'"
            />
          </div>
          <!-- End Search Input -->
        </div>
        <div
          class="flex items-center justify-between"
          :class="showMappersWithValues ? '' : ''"
        >
          <div></div>
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-2">
              <label class="text-sm text-gray-700">Nur Felder mit Werten anzeigen</label>
              <Switch
                v-model="showMappersWithValues"
                :checked="showMappersWithValues"
                class="data-[state=checked]:bg-purple-700 data-[state=unchecked]:bg-gray-200"
                thumb-class=""
                @update:checked="handleSwitchChange"
              />
            </div>
          </div>
        </div>
        <!-- End Col -->
      </div>
      <DatamapperTable
        :show-keys="shouldDisplayMapperKeys"
        :object="caseRecord"
        :is-entity="false"
        :mappers="caseRecord?.data.mappers"
        :mode="'edit'"
        @on-value-change="handleSave"
      />
    </div>
  </div>
</template>
